/**
 * Buyer Dashboard & Components
 * For buyers to browse, favorite, enquire, and communicate with breeders
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart,
  MessageSquare,
  Search,
  Bell,
  User,
  MapPin,
  Phone,
  Mail,
  Send,
  Clock,
  CheckCircle,
  Cat,
  Calendar,
  ChevronRight,
  Star,
  Filter
} from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Alert, Tabs } from '../shared/UIComponents';
import { formatCurrency, formatDate, formatRelativeDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs,
  getDoc,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

export const BuyerDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [activeTab, setActiveTab] = useState('favorites');

  useEffect(() => {
    if (user?.uid) {
      loadData();
    }
  }, [user?.uid]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load favorites
      const favsQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', user.uid)
      );
      const favsSnap = await getDocs(favsQuery);
      const favsData = favsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Fetch cat details for each favorite
      for (let fav of favsData) {
        if (fav.catId) {
          const catDoc = await getDoc(doc(db, 'cats', fav.catId));
          if (catDoc.exists()) {
            fav.cat = { id: catDoc.id, ...catDoc.data() };
          }
        }
        if (fav.kittenId) {
          const kittenDoc = await getDoc(doc(db, 'kittens', fav.kittenId));
          if (kittenDoc.exists()) {
            fav.cat = { id: kittenDoc.id, ...kittenDoc.data(), isKitten: true };
          }
        }
      }
      setFavorites(favsData.filter(f => f.cat));
      
      // Load enquiries
      const enquiriesQuery = query(
        collection(db, 'enquiries'),
        where('buyerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const enquiriesSnap = await getDocs(enquiriesQuery);
      setEnquiries(enquiriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
    } catch (error) {
      console.error('Error loading buyer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favId) => {
    try {
      await deleteDoc(doc(db, 'favorites', favId));
      setFavorites(favorites.filter(f => f.id !== favId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  // Count unread messages
  const unreadCount = enquiries.filter(e => e.hasNewReply).length;

  const tabs = [
    { id: 'favorites', label: 'My Favorites', count: favorites.length },
    { id: 'enquiries', label: 'My Enquiries', count: enquiries.length },
    { id: 'messages', label: 'Messages', count: unreadCount > 0 ? unreadCount : null }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-gray-900">My Dashboard</h1>
        <p className="text-gray-500">Track your favorite cats and conversations with breeders</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
            <Heart className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Favorites</p>
            <p className="text-2xl font-bold text-gray-900">{favorites.length}</p>
          </div>
        </Card>
        
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Enquiries</p>
            <p className="text-2xl font-bold text-gray-900">{enquiries.length}</p>
          </div>
        </Card>
        
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Bell className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Unread</p>
            <p className="text-2xl font-bold text-gray-900">{unreadCount}</p>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Content */}
      <div className="mt-6">
        {activeTab === 'favorites' && (
          <FavoritesList 
            favorites={favorites}
            loading={loading}
            onRemove={removeFavorite}
          />
        )}
        
        {activeTab === 'enquiries' && (
          <EnquiriesList 
            enquiries={enquiries}
            loading={loading}
            onRefresh={loadData}
          />
        )}
        
        {activeTab === 'messages' && (
          <MessagesList 
            enquiries={enquiries.filter(e => e.messages?.length > 0)}
            loading={loading}
            onRefresh={loadData}
          />
        )}
      </div>

      {/* Browse CTA */}
      <Card className="mt-8 text-center py-8 bg-gradient-to-r from-feline-50 to-paw-50">
        <Cat className="w-12 h-12 text-feline-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Looking for more cats?</h3>
        <p className="text-gray-500 mb-4">Browse our marketplace to find your perfect companion</p>
        <Link to="/marketplace">
          <Button>
            <Search className="w-4 h-4" />
            Browse Marketplace
          </Button>
        </Link>
      </Card>
    </div>
  );
};

// Favorites List
const FavoritesList = ({ favorites, loading, onRemove }) => {
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(3)].map((_, i) => (
        <Card key={i} className="animate-pulse"><div className="h-48 bg-gray-100 rounded-lg" /></Card>
      ))}
    </div>;
  }

  if (favorites.length === 0) {
    return (
      <Card className="text-center py-12">
        <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Favorites Yet</h3>
        <p className="text-gray-500 mb-4">
          Browse the marketplace and click the heart icon to save cats you love
        </p>
        <Link to="/marketplace">
          <Button variant="outline">Browse Cats</Button>
        </Link>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {favorites.map((fav) => (
        <FavoriteCard key={fav.id} favorite={fav} onRemove={() => onRemove(fav.id)} />
      ))}
    </div>
  );
};

// Favorite Card
const FavoriteCard = ({ favorite, onRemove }) => {
  const cat = favorite.cat;
  const mainImage = cat?.photos?.[0] || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f4f6' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23d1d5db' font-family='system-ui' font-size='48'%3E🐱%3C/text%3E%3C/svg%3E";

  const statusColors = {
    available: 'bg-paw-100 text-paw-700',
    reserved: 'bg-amber-100 text-amber-700',
    sold: 'bg-gray-100 text-gray-500'
  };

  return (
    <Card className="overflow-hidden group">
      <div className="relative h-48 -mx-6 -mt-6 mb-4">
        <img src={mainImage} alt={cat?.name} className="w-full h-full object-cover" />
        <div className="absolute top-3 right-3">
          <button
            onClick={(e) => { e.preventDefault(); onRemove(); }}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Heart className="w-5 h-5 text-pink-500" fill="#ec4899" />
          </button>
        </div>
        <div className="absolute top-3 left-3">
          <Badge className={statusColors[cat?.status] || statusColors.available}>
            {cat?.status}
          </Badge>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold text-gray-900">{cat?.name}</h3>
        <p className="text-sm text-gray-500">{cat?.breed}</p>
        
        {cat?.price && (
          <p className="text-lg font-bold text-feline-600">{formatCurrency(cat.price)}</p>
        )}

        <div className="flex gap-2 pt-2">
          <Link to={`/cat/${cat?.id}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full">View Details</Button>
          </Link>
          <Link to={`/cat/${cat?.id}?enquire=true`} className="flex-1">
            <Button size="sm" className="w-full">Enquire</Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};

// Enquiries List
const EnquiriesList = ({ enquiries, loading, onRefresh }) => {
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  if (loading) {
    return <Card><div className="animate-pulse space-y-4">
      {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-lg" />)}
    </div></Card>;
  }

  if (enquiries.length === 0) {
    return (
      <Card className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Enquiries Yet</h3>
        <p className="text-gray-500">
          When you contact a breeder about a cat, your enquiries will appear here
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="overflow-hidden p-0">
        <div className="divide-y divide-gray-200">
          {enquiries.map((enquiry) => (
            <div 
              key={enquiry.id}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-4"
              onClick={() => { setSelectedEnquiry(enquiry); setShowEnquiryModal(true); }}
            >
              <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden">
                {enquiry.catPhoto ? (
                  <img src={enquiry.catPhoto} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl">🐱</div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">{enquiry.catName || 'Cat Enquiry'}</p>
                  {enquiry.hasNewReply && (
                    <Badge variant="primary" className="text-xs">New Reply</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 truncate">
                  {enquiry.catteryName} • {formatRelativeDate(enquiry.createdAt)}
                </p>
              </div>

              <Badge className={cn(
                enquiry.status === 'new' ? 'bg-blue-100 text-blue-700' :
                enquiry.status === 'replied' ? 'bg-paw-100 text-paw-700' :
                enquiry.status === 'viewing_scheduled' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-700'
              )}>
                {enquiry.status?.replace('_', ' ')}
              </Badge>

              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          ))}
        </div>
      </Card>

      {/* Enquiry Detail Modal */}
      <EnquiryDetailModal
        isOpen={showEnquiryModal}
        onClose={() => { setShowEnquiryModal(false); setSelectedEnquiry(null); }}
        enquiry={selectedEnquiry}
        onRefresh={onRefresh}
      />
    </>
  );
};

// Messages List (filtered enquiries with messages)
const MessagesList = ({ enquiries, loading, onRefresh }) => {
  if (loading) {
    return <Card><div className="animate-pulse"><div className="h-48 bg-gray-100 rounded-lg" /></div></Card>;
  }

  if (enquiries.length === 0) {
    return (
      <Card className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Messages Yet</h3>
        <p className="text-gray-500">
          When breeders reply to your enquiries, conversations will appear here
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <p className="text-gray-500">Your conversations with breeders will appear here.</p>
      {/* TODO: Full messaging UI */}
    </Card>
  );
};

// Enquiry Detail Modal with messaging
const EnquiryDetailModal = ({ isOpen, onClose, enquiry, onRefresh }) => {
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (enquiry?.id) {
      // Load messages
      const loadMessages = async () => {
        const messagesQuery = query(
          collection(db, 'enquiries', enquiry.id, 'messages'),
          orderBy('createdAt', 'asc')
        );
        const messagesSnap = await getDocs(messagesQuery);
        setMessages(messagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      };
      loadMessages();

      // Mark as read
      if (enquiry.hasNewReply) {
        updateDoc(doc(db, 'enquiries', enquiry.id), { hasNewReply: false });
      }
    }
  }, [enquiry?.id]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setSending(true);
      
      // Add message to subcollection
      await addDoc(collection(db, 'enquiries', enquiry.id, 'messages'), {
        text: newMessage,
        senderId: user.uid,
        senderName: user.displayName || 'Buyer',
        senderType: 'buyer',
        createdAt: serverTimestamp()
      });

      // Update enquiry
      await updateDoc(doc(db, 'enquiries', enquiry.id), {
        lastMessage: newMessage,
        lastMessageAt: serverTimestamp(),
        hasNewMessage: true, // For breeder
        updatedAt: serverTimestamp()
      });

      setNewMessage('');
      
      // Reload messages
      const messagesQuery = query(
        collection(db, 'enquiries', enquiry.id, 'messages'),
        orderBy('createdAt', 'asc')
      );
      const messagesSnap = await getDocs(messagesQuery);
      setMessages(messagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  if (!enquiry) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enquiry Details" size="lg">
      <div className="space-y-4">
        {/* Cat Info */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
          <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
            {enquiry.catPhoto ? (
              <img src={enquiry.catPhoto} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl">🐱</div>
            )}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{enquiry.catName}</p>
            <p className="text-sm text-gray-500">{enquiry.catteryName}</p>
            {enquiry.catPrice && (
              <p className="text-feline-600 font-bold">{formatCurrency(enquiry.catPrice)}</p>
            )}
          </div>
        </div>

        {/* Original Enquiry */}
        <div className="p-4 bg-blue-50 rounded-xl">
          <p className="text-sm text-blue-600 mb-1">Your enquiry • {formatDate(enquiry.createdAt)}</p>
          <p className="text-gray-800">{enquiry.message}</p>
        </div>

        {/* Messages */}
        <div className="max-h-64 overflow-y-auto space-y-3">
          {messages.map((msg) => (
            <div 
              key={msg.id}
              className={cn(
                'p-3 rounded-xl max-w-[80%]',
                msg.senderType === 'buyer' 
                  ? 'bg-feline-100 ml-auto' 
                  : 'bg-gray-100'
              )}
            >
              <p className="text-sm text-gray-800">{msg.text}</p>
              <p className="text-xs text-gray-500 mt-1">
                {msg.senderName} • {formatRelativeDate(msg.createdAt)}
              </p>
            </div>
          ))}
        </div>

        {/* Message Input */}
        <div className="flex gap-2 pt-4 border-t">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1"
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          />
          <Button onClick={sendMessage} loading={sending}>
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Breeder Contact */}
        {enquiry.breederPhone && (
          <div className="pt-4 border-t">
            <p className="text-sm text-gray-500 mb-2">Breeder Contact</p>
            <div className="flex gap-4">
              {enquiry.breederPhone && (
                <a href={`tel:${enquiry.breederPhone}`} className="flex items-center gap-2 text-feline-600 hover:underline">
                  <Phone className="w-4 h-4" />
                  {enquiry.breederPhone}
                </a>
              )}
              {enquiry.breederWhatsapp && (
                <a href={`https://wa.me/${enquiry.breederWhatsapp.replace(/\D/g, '')}`} target="_blank" className="flex items-center gap-2 text-paw-600 hover:underline">
                  <MessageSquare className="w-4 h-4" />
                  WhatsApp
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Enquiry Form Component (for cat detail page)
export const EnquiryForm = ({ cat, cattery, onSuccess }) => {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message) return;

    try {
      setSubmitting(true);

      await addDoc(collection(db, 'enquiries'), {
        // Cat info
        catId: cat.id,
        catName: cat.name,
        catPhoto: cat.photos?.[0] || null,
        catPrice: cat.price,
        isKitten: cat.isKitten || false,
        
        // Cattery info
        catteryId: cattery.id,
        catteryName: cattery.catteryName,
        breederId: cattery.ownerId,
        breederPhone: cattery.phone,
        breederWhatsapp: cattery.whatsapp,
        
        // Buyer info
        buyerId: user?.uid || null,
        buyerName: formData.name,
        buyerEmail: formData.email,
        buyerPhone: formData.phone,
        
        // Message
        message: formData.message,
        
        // Status
        status: 'new',
        hasNewReply: false,
        hasNewMessage: true, // For breeder
        
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error('Error submitting enquiry:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Alert type="success">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-paw-500 mx-auto mb-2" />
          <p className="font-semibold">Enquiry Sent!</p>
          <p className="text-sm mt-1">The breeder will get back to you soon.</p>
        </div>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-semibold text-gray-900">Contact Breeder</h3>

      {!isAuthenticated && (
        <>
          <Input
            label="Your Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Email *"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </>
      )}

      <Input
        label="Phone Number"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        placeholder="082 123 4567"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={4}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
          placeholder={`Hi, I'm interested in ${cat.name}. Is this cat still available? I would love to schedule a viewing.`}
          required
        />
      </div>

      <Button type="submit" className="w-full" loading={submitting}>
        <Send className="w-4 h-4" />
        Send Enquiry
      </Button>

      {!isAuthenticated && (
        <p className="text-xs text-center text-gray-500">
          <Link to="/register" className="text-feline-600 hover:underline">Create an account</Link> to track your enquiries
        </p>
      )}
    </form>
  );
};

export default BuyerDashboard;
